import React, { Children, Ref } from 'react';
import { Box, BoxProps, Flex, Grid, GridItem, Stack, StackProps, Center } from '@chakra-ui/react';

type Props = BoxProps &
  Omit<StackProps, 'wrap'> & {
    variant?: 'center' | 'grid' | 'flex' | 'container' | 'box' | 'device-frame' | 'stack';
    wrap?: boolean;
    ref?: Ref<HTMLDivElement>;
    passPropsDownstream?: boolean;
  };

export const Layout = React.forwardRef<HTMLDivElement, Props>(
  ({ variant = 'box', wrap, children, ...rest }: Props, ref: React.ForwardedRef<HTMLDivElement>) => {
    /**
     * DOM
     */
    if (variant === 'center') {
      return (
        <Center data-object-type="core-layout-center" ref={ref} {...rest}>
          {children}
        </Center>
      );
    }
    if (variant === 'stack') {
      return (
        <Stack
          data-object-type="core-layout-stack"
          {...(wrap && { direction: 'row', flexWrap: 'wrap' })}
          ref={ref}
          {...rest}
        >
          {rest?.passPropsDownstream
            ? Children.map(children, child => {
                if (React.isValidElement(child)) {
                  return React.cloneElement(child, { ...rest });
                }
                return child;
              })
            : children}
        </Stack>
      );
    }
    if (variant === 'flex') {
      return (
        <Flex data-object-type="core-layout-flex" ref={ref} {...rest}>
          {children}
        </Flex>
      );
    }
    if (variant === 'grid') {
      return (
        <Grid ref={ref} data-object-type="core-layout-grid" {...rest}>
          {Children.map(children, child => {
            if (React.isValidElement(child)) {
              return (
                <GridItem key={child.key} display={child.props.display} colSpan={child?.props?.colSpan || 1}>
                  {child}
                </GridItem>
              );
            }
            return child;
          })}
        </Grid>
      );
    }
    if (variant === 'container') {
      return (
        <Box ref={ref} data-object-type="core-layout-container" {...rest}>
          {children}
        </Box>
      );
    }
    return (
      <Box ref={ref} data-object-type="core-layout-box" {...rest}>
        {children}
      </Box>
    );
  }
);
Layout.displayName = 'Layout';
